import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantStore } from "@/stores/restaurantStore";
import { usePosIdentityStore } from "@/stores/posIdentityStore";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  /** Last Supabase auth event — useful for reacting to PASSWORD_RECOVERY, TOKEN_REFRESHED, etc. */
  event: AuthChangeEvent | null;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [event, setEvent] = useState<AuthChangeEvent | null>(null);
  const previousUserIdRef = useRef<string | null>(null);
  const clearRestaurantState = useRestaurantStore(
    (s) => s.clearRestaurantState
  );
  const clearPosIdentity = usePosIdentityStore((s) => s.clear);

  const resetSessionScopedState = useCallback(() => {
    clearRestaurantState();
    clearPosIdentity();
  }, [clearRestaurantState, clearPosIdentity]);

  const refreshSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session ?? null);
    setStatus(data.session ? "authenticated" : "unauthenticated");
    if (!data.session) {
      previousUserIdRef.current = null;
      resetSessionScopedState();
    }
  }, [resetSessionScopedState]);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION synchronously on subscribe,
    // so we don't need a separate getSession() call.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((authEvent, nextSession) => {
      setEvent(authEvent);
      setSession(nextSession);
      setStatus(nextSession ? "authenticated" : "unauthenticated");

      // Supabase fires SIGNED_IN on every page load when a session exists, so
      // we cannot treat SIGNED_IN alone as "a new user signed in". Instead,
      // compare user ids: reset only when the session disappears OR the user
      // actually changes (shared device handover). Same user reloading the
      // page preserves their tenant selection and PIN identity.
      const nextUserId = nextSession?.user.id ?? null;
      const previousUserId = previousUserIdRef.current;
      const userChanged =
        previousUserId !== null && nextUserId !== previousUserId;

      if (!nextSession || userChanged) {
        resetSessionScopedState();
      }

      previousUserIdRef.current = nextUserId;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [resetSessionScopedState]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      event,
      refreshSession,
    }),
    [status, session, event, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* eslint-disable react-refresh/only-export-components -- hook colocated with provider */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
